
import { useState } from 'react';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
      <div className="container mx-auto px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 group cursor-pointer">
            <div className="relative">
              <div className="w-12 h-12 flex items-center justify-center group-hover:scale-105 transition-all duration-300 relative">
                {/* 地震波纹效果 - 简约版 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 border border-teal-400/40 rounded-full animate-pulse"></div>
                  <div className="absolute w-8 h-8 border border-teal-400/60 rounded-full animate-ping"></div>
                  <div className="absolute w-6 h-6 border border-cyan-400/50 rounded-full"></div>
                </div>
                
                {/* 中心探测点 */}
                <div className="relative z-10 w-2 h-2 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full shadow-lg"></div>
                
                {/* 地震波线条 - 简化版 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-px bg-gradient-to-r from-transparent via-teal-400/80 to-transparent transform rotate-45"></div>
                  <div className="absolute w-8 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent transform -rotate-45"></div>
                  <div className="absolute w-6 h-px bg-gradient-to-r from-transparent via-teal-400/70 to-transparent"></div>
                  <div className="absolute w-px h-6 bg-gradient-to-b from-transparent via-cyan-400/70 to-transparent"></div>
                </div>
                
                {/* 悬停光晕效果 */}
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400/0 via-teal-400/20 to-cyan-400/0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-light text-white tracking-wide" style={{ fontFamily: '"Orbitron", monospace' }}>
                Geo<span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent font-normal">Vantage</span>
              </h1>
              <div className="text-xs text-teal-400 font-medium tracking-wider">GEOPHYSICAL SOLUTIONS</div>
            </div>
          </div>
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <button 
              onClick={() => scrollToSection('home')}
              className="px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('equipment')}
              className="px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer"
            >
              Equipment
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('coverage')}
              className="px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer"
            >
              Coverage
            </button>
            
            {/* Email Portal Button */}
            <a 
              href="http://email.geovantage.ae/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-6 py-3 text-gray-300 hover:text-white font-medium transition-all duration-300 hover:bg-white/5 rounded-xl whitespace-nowrap cursor-pointer flex items-center space-x-2"
            >
              <i className="ri-mail-line text-lg group-hover:scale-110 transition-transform duration-300"></i>
              <span>Email</span>
              <i className="ri-external-link-line text-xs opacity-60 group-hover:opacity-100 transition-opacity duration-300"></i>
            </a>
            
            <button 
              onClick={() => scrollToSection('contact')}
              className="ml-4 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-teal-500/25 transform hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-xl transition-colors duration-300 cursor-pointer"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-white/10">
            <nav className="flex flex-col space-y-2">
              <button 
                onClick={() => scrollToSection('home')}
                className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('equipment')}
                className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Equipment
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('coverage')}
                className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Coverage
              </button>
              
              {/* Mobile Email Portal */}
              <a 
                href="http://email.geovantage.ae/"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-left transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center space-x-2"
              >
                <i className="ri-mail-line text-lg group-hover:scale-110 transition-transform duration-300"></i>
                <span>Email Portal</span>
                <i className="ri-external-link-line text-xs opacity-60 group-hover:opacity-100 transition-opacity duration-300"></i>
              </a>
              
              <button 
                onClick={() => scrollToSection('contact')}
                className="mx-4 mt-4 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-medium rounded-xl transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
